import { cn } from "@/lib/utils";
import { normalizeAvailability } from "@/lib/realtime/presence-service";

// One visual vocabulary for staff presence across Chats surfaces (thread list, header,
// participants panel, new-chat picker, profile chip). Offline renders nothing.
const PRESENCE_VISUALS = {
  available: { dotClassName: "bg-emerald-500", label: "Available" },
  busy: { dotClassName: "bg-rose-500", label: "Busy" },
  meeting: { dotClassName: "bg-amber-500", label: "In a meeting" },
  away: { dotClassName: "bg-slate-400", label: "Away" },
  // Teams-style: red with a tiny white bar, so DND reads differently from plain busy.
  dnd: { dotClassName: "bg-rose-600", label: "Do not disturb", showDndBar: true },
};

export function getPresenceVisual(availability) {
  return PRESENCE_VISUALS[normalizeAvailability(availability)];
}

// Picker order mirrors severity: available first, do-not-disturb last.
export const AVAILABILITY_OPTIONS = [
  { value: "available", label: "Available" },
  { value: "busy", label: "Busy" },
  { value: "meeting", label: "In a meeting" },
  { value: "away", label: "Away" },
  { value: "dnd", label: "Do not disturb" },
];

/**
 * Colored presence dot. Renders nothing while offline; the color encodes the user's
 * self-set availability (green available, red busy, amber meeting, slate away, DND bar).
 */
export default function PresenceDot({ online = true, availability, className, title }) {
  if (!online) return null;
  const visual = getPresenceVisual(availability);
  return (
    <span
      className={cn(
        "inline-flex h-2.5 w-2.5 shrink-0 items-center justify-center rounded-full ring-2 ring-background",
        visual.dotClassName,
        className,
      )}
      title={title ?? visual.label}
      aria-label={visual.label}
    >
      {visual.showDndBar ? <span className="h-[2px] w-[5px] rounded-full bg-white" aria-hidden /> : null}
    </span>
  );
}
